import {observer} from 'mobx-react-lite'
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import tw from 'tailwind-styled-components'
import {ObjUtil} from 'yougile-util'
import {DataObjectId, Hub} from 'yougile-model'
import {Items} from '@api/model/items'
import {UiLocationProvider} from '@api/shared/context/ui-location'
import {Sync} from '@client/sync'
import {AddTask} from '@components/add-task'
import {useBoardStore} from '@components/board/board-store'
import {MirrorColumnPath} from '@components/columns/mirror-column/mirror-column-path'
import {ShowArchivedButton} from '@components/columns/show-archived-button'
import {ShowMoreButton} from '@components/columns/show-more-button'
import {Loader} from '@components/common/base/loader'
import {WithPerfectScrollbar} from '@components/common/with-scrollbar/with-scrollbar'
import {useDnDStore, WithDraggable} from '@components/draggable'
import {Droppable, WithDroppable} from '@components/droppable'
import {TaskCard, taskPlaceholderMixin} from '@components/task-card'
import {useForceUpdate, useSyncEvent} from '@containers/hooks'
import {cn} from '@containers/html-utils'
import {
  QueuedRender,
  taskCardPlaceholderClassName
} from '@containers/queued-render'
import {getScrollYToCenterDelta} from '@containers/scroll-utils'
import {TimeoutRender} from '@containers/timeout-render'
import {Column} from '@extensions/column'
import {globals} from '@extensions/globals-base'
import {getGroupLocationPath, useColumnStore} from '../column-store'
import {TwSubtaskPlaceholder} from '../containers'

export const MirrorColumn = observer(() => {
  const [state, store] = useColumnStore()
  const [boardState, boardStore] = useBoardStore()
  const group = store.column

  const forceUpdate = useForceUpdate()
  useSyncEvent(
    'custom',
    {filter: group?.id, companyId: group?.companyId, debounceTime: 50},
    forceUpdate,
    [group]
  )

  const columns = state.loading ? [] : Column.getMirroredColumns(group)
  const setContainerEl = useScrollObserver(columns)
  useScrollToFirstArchived()

  return (
    <WithPerfectScrollbar
      ref={x => x && store.setScrollBar(x)}
      containerRef={el => {
        setContainerEl(el)
        store.setScrollableEl(el)
      }}
      className="relative px-8 -mx-8 pb-4 column-scroll-wrapper"
      onMouseDown={boardStore.boardDragScroll()}
      wheelPropagation
      wheelSpeed={1.5}
      onScroll={boardState.tasksRenderQueue.requestToUpdatePriorities}
    >
      {columns.map((column, ix) => (
        <UiLocationProvider location="mirror-column" key={column.id}>
          <SingleGroupWithSourceUpdateOnDrop column={column} ix={ix} />
        </UiLocationProvider>
      ))}
    </WithPerfectScrollbar>
  )
})

interface SingleColumnProps {
  column: Hub
  ix: number
}

// eslint-disable-next-line max-lines-per-function
const SingleGroup = observer(({column, ix}: SingleColumnProps) => {
  const [state, store] = useColumnStore()
  const [boardState, boardStore] = useBoardStore()
  const tasksOpenSorted = store.getOpenSortedTasks(column)
  const [showLoader, setShowLoader] = useState(!!tasksOpenSorted.length)
  const hideLoader = () => setShowLoader(false)
  const parentGroup = store.column

  const forceUpdate = useForceUpdate()
  useSyncEvent(
    'custom',
    {filter: column?.id, companyId: column?.companyId, debounceTime: 50},
    forceUpdate,
    [column]
  )

  const tasksArchived = store.getClosedTasks(column)
  const hidden = Items.isHidden(Items.get(column.id))
  const tasksLeft =
    store.getOpenTasks(column).length - store.getTaskCountLimit(column)
  const tasksArchivedLeft =
    tasksArchived.length - store.getTaskCountLimit(column)

  // Small padding is required for task outline not to be cropped due to overflow hidden
  const antiCropPadTop = !!tasksOpenSorted.length
  const antiCropPadBottom =
    (!!tasksOpenSorted.length && !tasksArchived.length) ||
    (!!tasksArchived.length && state.expandedCompleted[parentGroup.id])

  const shouldShowCustomPlaceholder =
    state.mirrorColumnPlaceholder?.columnId === parentGroup.id &&
    state.mirrorColumnPlaceholder?.mirroredColumnId === column.id
  const customPlaceholderHeight = state.mirrorColumnPlaceholder?.height

  return (
    <TwMirroredGroup
      data-column-id={column.id}
      $hidden={hidden}
      className={cn(ix > 0 && 'mt-8')}
      // Each column in a mirror column must handle native DnD separately
      onDragOver={e => store.onSubtasksColumnDragOver(e, column?.id)}
      onDragLeave={() => store.onSubtasksColumnDragLeave(column?.id)}
      onDrop={() => store.onSubtaskColumnDrop(column)}
    >
      {ix > 0 && (
        <>
          <MirrorColumnPath
            items={getGroupLocationPath(column)}
            className="px-12"
          />
          <AddTask
            columnId={column.id}
            scrollTo="task-center"
            className="mt-4 mx-0"
          />
        </>
      )}

      <div
        className={cn(
          'proj-task-container',
          'space-y-8',
          antiCropPadTop && 'pt-2',
          antiCropPadBottom && 'pb-2'
        )}
      >
        <TwSubtaskPlaceholder className="hidden" />

        <Droppable
          accepts={['task']}
          container=".task-group-container-mirrored-group"
          showPlaceholder
          onDrop={drag =>
            boardStore.onTaskDrop({
              drag,
              toColumn: column,
              columnStore: store
            })
          }
          className="empty:hidden"
        />

        {shouldShowCustomPlaceholder && (
          <div
            className={taskPlaceholderMixin}
            style={{height: customPlaceholderHeight}}
          />
        )}

        {tasksOpenSorted.map((task, i) => (
          <QueuedRender
            key={task.id}
            queue={boardState.tasksRenderQueue}
            id={parentGroup?.location}
            placeholderClassName={taskCardPlaceholderClassName}
          >
            <WithDroppable
              key={task.id}
              accepts={['task']}
              container=".task-group-container-mirrored-group"
              showPlaceholder
              onDrop={(drag, after) =>
                boardStore.onTaskDrop({
                  drag,
                  toColumn: column,
                  taskId: task.id,
                  after,
                  columnStore: store
                })
              }
            >
              <WithDraggable
                disabled={!globals.dragAllowed(task.id)}
                dropArea={boardState.dropArea}
                data={{
                  type: 'task',
                  payload: {taskId: task.id, columnId: column.id}
                }}
                clickablesOnHover
                placeholder={getPlaceholder}
                ghost={getGhost}
                scrollContainers={[
                  {selector: '.proj-task-list', direction: 'x'}
                ]}
                trigger="middleY"
              >
                <TaskCard
                  task={task}
                  containerId={parentGroup.id}
                  onClick={e => store.selectMultipleTasksId(task.id, e)}
                  style={{zIndex: 1000 - i}}
                  className={cn(
                    'proj-task-item',
                    Items.isHidden(Items.get(task.id)) && 'api-hide',
                    i > 0 && 'mt-8'
                  )}
                  boardId={boardStore?.board?.id}
                />
              </WithDraggable>
            </WithDroppable>
          </QueuedRender>
        ))}

        {showLoader && (
          <TimeoutRender timeout={1000}>
            <div className="text-center">
              <Loader size="xsmall" />
            </div>
          </TimeoutRender>
        )}

        <QueuedRender
          queue={boardState.tasksRenderQueue}
          id={parentGroup?.location}
          onBeforeRender={hideLoader}
        >
          <>
            {tasksLeft > 0 && (
              <ShowMoreButton
                count={tasksLeft}
                onClick={() => {
                  store.taskCountShowMore(column)
                  forceUpdate()
                }}
                className="mt-8"
                noBorder={globals.theme === 'light'}
              />
            )}

            <div className="task-dropzone hidden" />

            {tasksArchived.length > 0 && (
              <>
                <ShowArchivedButton
                  count={tasksArchived.length}
                  expanded={state.expandedCompleted[column.id]}
                  onClick={() => store.toggleExpandedCompleted(column.id)}
                  className={cn(!!tasksOpenSorted.length && 'mt-8')}
                />

                {state.expandedCompleted[column.id] && (
                  <div className="proj-task-container mt-8 space-y-8">
                    {store.getClosedSortedTasks(column).map(task => (
                      <TaskCard
                        key={task.id}
                        task={task}
                        containerId={parentGroup.id}
                        onClick={e => store.selectMultipleTasksId(task.id, e)}
                        className={cn(
                          'proj-task-item',
                          Items.isHidden(Items.get(task.id)) && 'api-hide'
                        )}
                        boardId={boardStore?.board?.id}
                      />
                    ))}

                    {tasksArchivedLeft > 0 && (
                      <ShowMoreButton
                        count={tasksArchivedLeft}
                        onClick={() => store.taskCountShowMore(column)}
                        noBorder={globals.theme === 'light'}
                      />
                    )}
                  </div>
                )}
              </>
            )}
          </>
        </QueuedRender>
      </div>
    </TwMirroredGroup>
  )
})

const SingleGroupWithSourceUpdateOnDrop = observer(
  (props: SingleColumnProps) => {
    const [dndState] = useDnDStore()
    const [, store] = useColumnStore()

    useLayoutEffect(() => {
      !dndState.dragStarted &&
        store.updateSourceColumnOnDrop(dndState.draggableProps, props.column)
    }, [dndState.dragStarted])

    return <SingleGroup {...props} />
  }
)

const TwMirroredGroup = tw.div<{$hidden?: boolean}>`
  task-group-container-mirrored-group
  ${p => cn(p.$hidden && '!hidden')}
`

const useScrollObserver = (columns: Hub[]): ((el: HTMLElement) => void) => {
  const [, store] = useColumnStore()
  const box = Sync.getCurrentSandbox()
  const [containerEl, setContainerEl] = useState<HTMLElement | undefined>()
  const observer = useMemo(() => {
    if (!containerEl) {
      return undefined
    }

    const set = new Set<Element>()
    const cb = (entries: IntersectionObserverEntry[]) => {
      entries.forEach(x => {
        x.isIntersecting ? set.add(x.target) : set.delete(x.target)
      })
      const firstCol = Array.from(set)
        .sort(
          (a, b) =>
            a.getBoundingClientRect().top - b.getBoundingClientRect().top
        )
        .shift()
      const columnId = firstCol?.getAttribute('data-column-id')
      store.setVisibleColumnId(columnId as DataObjectId)
    }
    return new IntersectionObserver(cb, {
      root: containerEl,
      threshold: [0, 0.25, 0.5, 0.75, 1]
    })
  }, [containerEl, box, store])
  useEffect(() => () => observer?.disconnect(), [observer])
  useEffect(() => {
    if (containerEl && observer) {
      const els = containerEl.querySelectorAll(
        '.task-group-container-mirrored-group'
      )
      Array.from(els).forEach(el => observer.observe(el))
    }
  }, [columns, containerEl, observer])
  return setContainerEl
}

const useScrollToFirstArchived = () => {
  const [, store] = useColumnStore()
  const currExpanded = store.state.expandedCompleted
  const prevExpandedRef = useRef(currExpanded)

  useEffect(() => {
    const toggledColumnIds = ObjUtil.getChangedKeys(
      prevExpandedRef.current,
      currExpanded
    ) as Set<DataObjectId>
    prevExpandedRef.current = currExpanded

    const firstToggledColumnId = Array.from(toggledColumnIds.values())[0]
    if (firstToggledColumnId && currExpanded[firstToggledColumnId]) {
      const firstTaskIdInToggledColumn =
        store.state.closedTaskListSorted.get(firstToggledColumnId)?.[0]?.id
      const scrollableEl = store.state.scrollableEl
      const taskEl = scrollableEl?.querySelector(
        `[data-task-id="${firstTaskIdInToggledColumn}"]`
      )
      if (scrollableEl && taskEl) {
        const dy = getScrollYToCenterDelta(scrollableEl, taskEl)
        scrollableEl.scrollTop += dy
      }
    }
  }, [prevExpandedRef, currExpanded, store])
}

const getPlaceholder = (rect: DOMRect) => {
  const placeholder = document.createElement('div')
  placeholder.style.height = `${rect.height}px`
  placeholder.className = taskPlaceholderMixin + ' mb-6'
  return placeholder
}

const getGhost = (ghost: HTMLElement) => {
  ghost.classList.add('!shadow-lg')
  return ghost
}
